import React, { useState } from 'react';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { SaveAgentAction } from '../../agent/save-agent.action';
import { SaveDocumentAction } from '../../document/save-document.action';
import { SaveWorkflowAction } from '../../workflow/save-workflow.action';
import {
  collectDirtySavableArtifacts,
  isSaveArtifactsApplicable,
  resolveSavableModelForDirtyEntry,
  type DirtyArtifactEntry,
} from '@cotera/client/v0/chat/artifact-dirty-state';

const saveChatArtifactsParamsSchema = z.object({
  /** Selected dirty entries to save (`resourceType:resourceId`). Defaults to all dirty. */
  resourceKeys: z.array(z.string()).optional(),
});

type Params = z.infer<typeof saveChatArtifactsParamsSchema>;

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

export function dirtyArtifactResourceKey(entry: DirtyArtifactEntry): string {
  return `${entry.resourceType}:${entry.resourceId}`;
}

export class SaveChatArtifactsAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Save Artifacts';
  description = 'Save unsaved changes across open chat artifacts';
  inputSchema = saveChatArtifactsParamsSchema;
  shortcut = ['ctrl', 's'];
  group = 'Chat';
  icon = 'save';

  constructor(private readonly modalManager: ModalManager) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return isSaveArtifactsApplicable(context);
  }

  shouldAsk(context: ApplicableContext): boolean {
    return collectDirtySavableArtifacts(context).length > 1;
  }

  ask(
    _params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const dirty = collectDirtySavableArtifacts(context);

    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async () => {
          await onCancel();
          this.modalManager.pop();
        };

        this.modalManager.push({
          view: () => (
            <SaveChatArtifactsAskView
              dirty={dirty}
              onRespond={async (nextParams) => {
                await onRespond(nextParams);
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const dirty = collectDirtySavableArtifacts(context);
    if (dirty.length === 0) {
      return ok({});
    }

    const selectedKeys =
      params.resourceKeys !== undefined
        ? new Set(params.resourceKeys)
        : new Set(dirty.map(dirtyArtifactResourceKey));

    const toSave = dirty.filter((entry) =>
      selectedKeys.has(dirtyArtifactResourceKey(entry))
    );

    if (toSave.length === 0) {
      return ok({ cancelled: true });
    }

    let savedCount = 0;
    const silent = toSave.length > 1;
    for (const entry of toSave) {
      // Skip entries whose model is no longer in scope. We resolve by id in the
      // sub-save (below) rather than retargeting: retargeting untargets sibling
      // models of the same type, and multiple same-type artifacts can be open.
      const model = resolveSavableModelForDirtyEntry(context, entry);
      if (model === null) {
        continue;
      }
      const result = await this.delegateSave(entry, context, silent);
      if (result.isErr()) {
        return result;
      }
      if (result.value.cancelled === true) {
        return result;
      }
      savedCount += 1;
    }

    if (savedCount > 1) {
      toast.success(`Saved ${savedCount} artifacts`);
    }

    return ok({});
  }

  private async delegateSave(
    entry: DirtyArtifactEntry,
    context: ExecuteContext,
    silent: boolean
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    switch (entry.type) {
      case 'agent': {
        const action = context.getAction(SaveAgentAction);
        if (action === undefined || action === null) {
          return err([
            { t: 'error', message: 'Save Agent action is not available' },
          ]);
        }
        return await action.askOrExecute(
          { silent, agentId: entry.resourceId },
          context
        );
      }
      case 'document': {
        const action = context.getAction(SaveDocumentAction);
        if (action === undefined || action === null) {
          return err([
            { t: 'error', message: 'Save Document action is not available' },
          ]);
        }
        return await action.askOrExecute(
          { silent, documentId: entry.resourceId },
          context
        );
      }
      case 'workflow': {
        const action = context.getAction(SaveWorkflowAction);
        if (action === undefined || action === null) {
          return err([
            { t: 'error', message: 'Save Workflow action is not available' },
          ]);
        }
        return await action.askOrExecute(
          { silent, workflowId: entry.resourceId },
          context
        );
      }
      default:
        return err([
          {
            t: 'error',
            message: `Unsupported artifact type for save: ${entry.type}`,
          },
        ]);
    }
  }
}

const SaveChatArtifactsAskView: React.FC<{
  dirty: DirtyArtifactEntry[];
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ dirty, onRespond, onCancel }) => {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
    () => new Set(dirty.map(dirtyArtifactResourceKey))
  );
  const [loading, setLoading] = useState(false);

  const toggle = (entry: DirtyArtifactEntry): void => {
    const key = dirtyArtifactResourceKey(entry);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSave = async (): Promise<void> => {
    if (loading || selectedKeys.size === 0) {
      return;
    }
    setLoading(true);
    await onRespond({ resourceKeys: Array.from(selectedKeys) });
    setLoading(false);
  };

  return (
    <ModalPageView
      title="Unsaved changes"
      subtitle="Choose which artifacts to save"
      className="px-4 py-4"
    >
      <div className="flex flex-col gap-4">
        <div className="flex max-h-[min(50vh,24rem)] flex-col gap-2 overflow-y-auto">
          {dirty.map((entry) => {
            const key = dirtyArtifactResourceKey(entry);
            const checked = selectedKeys.has(key);
            return (
              <label
                key={key}
                className="flex cursor-pointer items-center gap-3 rounded-md border border-input px-3 py-2 text-sm hover:bg-muted/40"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => {
                    toggle(entry);
                  }}
                />
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium">{entry.title}</span>
                  <span className="ml-2 text-xs text-muted-foreground capitalize">
                    {entry.type}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={loading || selectedKeys.size === 0}
            onClick={() => void handleSave()}
          >
            Save selected
          </Button>
        </div>
      </div>
    </ModalPageView>
  );
};
